import { useId, useMemo, useState } from 'react';
import { formatCurrency } from '../../PolicyAdmin/utils';
import type { TrendPoint } from '../types';
import { formatCompactCurrency } from '../utils';

const WIDTH = 760;
const HEIGHT = 260;
const PAD = { top: 16, right: 16, bottom: 32, left: 64 };
const INNER_W = WIDTH - PAD.left - PAD.right;
const INNER_H = HEIGHT - PAD.top - PAD.bottom;
const GRID_LINES = 4;

/** Round a domain bound up to a readable tick value (1/2/5 × a power of ten). */
function niceCeiling(value: number): number {
  if (value <= 0) return 0;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

export interface TrendChartProps {
  points: TrendPoint[];
  /** Optional caption rendered under the chart. */
  footnote?: string;
}

/**
 * Monthly gross written premium as an area chart. Hand-rolled SVG: the app has
 * no chart dependency, and a single series does not justify adding one.
 */
export const TrendChart: React.FC<TrendChartProps> = ({ points, footnote }) => {
  const gradientId = useId();
  const [hovered, setHovered] = useState<number | null>(null);

  const geometry = useMemo(() => {
    const values = points.map(p => p.grossWrittenPremium);
    const rawMax = Math.max(0, ...values);
    const rawMin = Math.min(0, ...values);
    const domainMax = niceCeiling(rawMax) || 1;
    const domainMin = rawMin < 0 ? -niceCeiling(Math.abs(rawMin)) : 0;
    const span = domainMax - domainMin || 1;

    const x = (index: number) =>
      points.length <= 1
        ? PAD.left + INNER_W / 2
        : PAD.left + (index / (points.length - 1)) * INNER_W;
    const y = (value: number) => PAD.top + INNER_H - ((value - domainMin) / span) * INNER_H;

    const coords = points.map((point, index) => ({ x: x(index), y: y(point.grossWrittenPremium) }));
    const line = coords
      .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`)
      .join(' ');
    const baseline = y(Math.max(domainMin, 0));
    const area = coords.length
      ? `${line} L${coords[coords.length - 1].x.toFixed(1)},${baseline.toFixed(1)} L${coords[0].x.toFixed(1)},${baseline.toFixed(1)} Z`
      : '';

    const ticks = Array.from({ length: GRID_LINES + 1 }, (_, i) => {
      const value = domainMin + (span * i) / GRID_LINES;
      return { value, y: y(value) };
    });

    return { coords, line, area, ticks, baseline };
  }, [points]);

  if (!points.length) {
    return (
      <p className="py-10 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
        No premium was written in this period.
      </p>
    );
  }

  const total = points.reduce((sum, p) => sum + p.grossWrittenPremium, 0);
  // With two years of months, every label would collide — show roughly six.
  const labelStride = Math.max(1, Math.ceil(points.length / 6));
  const active = hovered === null ? null : points[hovered];

  return (
    <div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-auto w-full"
        role="img"
        aria-label={`Gross written premium by month, ${points[0].label} to ${points[points.length - 1].label}, totalling ${formatCurrency(total)}`}
        onMouseLeave={() => setHovered(null)}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-primary-500)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--color-primary-500)" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {geometry.ticks.map(tick => (
          <g key={tick.value}>
            <line
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={tick.y}
              y2={tick.y}
              stroke="var(--color-border-light)"
              strokeWidth={1}
            />
            <text
              x={PAD.left - 10}
              y={tick.y + 4}
              textAnchor="end"
              fontSize={11}
              fill="var(--color-text-secondary)"
            >
              {formatCompactCurrency(tick.value)}
            </text>
          </g>
        ))}

        <path d={geometry.area} fill={`url(#${gradientId})`} />
        <path
          d={geometry.line}
          fill="none"
          stroke="var(--color-primary-500)"
          strokeWidth={2.5}
          strokeLinejoin="round"
        />

        {geometry.coords.map((coord, index) => (
          <circle
            key={points[index].month}
            cx={coord.x}
            cy={coord.y}
            r={hovered === index ? 5 : 3}
            fill="var(--color-background-paper)"
            stroke="var(--color-primary-500)"
            strokeWidth={2}
          />
        ))}

        {points.map((point, index) =>
          index % labelStride === 0 ? (
            <text
              key={`label-${point.month}`}
              x={geometry.coords[index].x}
              y={HEIGHT - 10}
              textAnchor="middle"
              fontSize={11}
              fill="var(--color-text-secondary)"
            >
              {point.label}
            </text>
          ) : null
        )}

        {/* Full-height hit areas so the pointer does not have to find the dot. */}
        {points.map((point, index) => {
          const bandWidth = INNER_W / Math.max(1, points.length - 1);
          return (
            <rect
              key={`hit-${point.month}`}
              x={geometry.coords[index].x - bandWidth / 2}
              y={PAD.top}
              width={bandWidth}
              height={INNER_H}
              fill="transparent"
              onMouseEnter={() => setHovered(index)}
            >
              <title>{`${point.label}: ${formatCurrency(point.grossWrittenPremium)} across ${point.transactionCount} transactions`}</title>
            </rect>
          );
        })}

        {active && hovered !== null && (
          <line
            x1={geometry.coords[hovered].x}
            x2={geometry.coords[hovered].x}
            y1={PAD.top}
            y2={PAD.top + INNER_H}
            stroke="var(--color-primary-500)"
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        )}
      </svg>

      <div
        className="mt-2 flex flex-wrap items-baseline justify-between gap-2 text-xs"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        <span>
          {active
            ? `${active.label}: ${formatCurrency(active.grossWrittenPremium)} · ${active.transactionCount} transactions`
            : (footnote ?? 'Hover a month for its premium and transaction count.')}
        </span>
        <span>Total {formatCurrency(total)}</span>
      </div>
    </div>
  );
};
